import * as React from 'react'
import { authService } from '@/services/AuthService'
import { staffTokenStorage as tokenStorage } from '@/helpers/tokenStorage'
import logger, { toErrorMessage } from '@/helpers/logger'
import { isSessionRejected } from '@/auth/sessionRejected'
import type { LoginInput, User } from '@/schemas/auth.schema'

interface AuthContextValue {
  /** The signed-in user, or `null` when signed out. */
  user: User | null
  /** The current access token, or `null` when signed out. */
  token: string | null
  /** True while the initial "restore session from token" check is in flight. */
  isLoading: boolean
  /** Whether a user is currently authenticated. */
  isAuthenticated: boolean
  /** Exchange credentials for a session; resolves once the user is loaded. */
  login: (input: LoginInput) => Promise<void>
  /** Clear the session and stored token. */
  logout: () => void
}

const AuthContext = React.createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: React.ReactNode
}

/**
 * Provides authentication state to the app. On mount it restores a session from
 * a persisted token (by fetching the current user); a failed restore clears the
 * token and drops to the signed-out state.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  // helpers → state → effects → handlers → render
  const [user, setUser] = React.useState<User | null>(null)
  const [token, setToken] = React.useState<string | null>(() => tokenStorage.get())
  const [isLoading, setIsLoading] = React.useState<boolean>(true)

  React.useEffect(() => {
    let active = true

    async function restoreSession(): Promise<void> {
      if (!tokenStorage.get()) {
        setIsLoading(false)
        return
      }
      try {
        const currentUser = await authService.me()
        if (active) setUser(currentUser)
      } catch (error) {
        logger.warn('Session restore failed', { error: toErrorMessage(error) })
        // Only a rejected token ends the session; an unreachable API (a deploy,
        // a dropped connection) leaves it for the next load to retry.
        if (isSessionRejected(error)) {
          tokenStorage.clear()
          if (active) setToken(null)
        }
        if (active) setUser(null)
      } finally {
        if (active) setIsLoading(false)
      }
    }

    void restoreSession()
    return () => {
      active = false
    }
  }, [])

  const login = React.useCallback(async (input: LoginInput): Promise<void> => {
    const result = await authService.login(input)
    tokenStorage.set(result.accessToken)
    setToken(result.accessToken)
    setUser(result.user)
  }, [])

  const logout = React.useCallback((): void => {
    void authService.logout().catch((error) => {
      logger.warn('Server logout failed', { error: toErrorMessage(error) })
    })
    tokenStorage.clear()
    setToken(null)
    setUser(null)
  }, [])

  const value: AuthContextValue = {
    user,
    token,
    isLoading,
    isAuthenticated: user !== null,
    login,
    logout,
  }

  return <AuthContext value={value}>{children}</AuthContext>
}

/**
 * Access the auth context. Throws if used outside an {@link AuthProvider} so a
 * misplaced consumer fails loudly instead of reading a stale default.
 */
export function useAuth(): AuthContextValue {
  const context = React.use(AuthContext)
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
